import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { AlertCircle, CheckCircle2, Mail, MapPin, Phone, Send } from "lucide-react";
import Banner from "@/components/Banner";
import { Eyebrow, GoldButton } from "@/components/Reveal";
import {
  PHOTOS,
  SERVICES,
  FREQUENCES,
  serviceSlugToName,
  PHONE_DISPLAY,
  PHONE_LINK,
  EMAIL,
  ZONE,
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  nom: "",
  entreprise: "",
  courriel: "",
  telephone: "",
  service: "",
  adresse: "",
  frequence: "",
  message: "",
  site_web: "",
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const PHONE_RE = /^[0-9+\-().\s]{7,20}$/;

const validate = (f) => {
  const e = {};
  if (f.nom.trim().length < 2) e.nom = "Veuillez indiquer votre nom.";
  if (f.entreprise.trim().length < 2)
    e.entreprise = "Veuillez indiquer votre entreprise ou le nom de l'immeuble.";
  if (!EMAIL_RE.test(f.courriel.trim()))
    e.courriel = "Veuillez indiquer une adresse courriel valide.";
  if (!PHONE_RE.test(f.telephone.trim()))
    e.telephone = "Veuillez indiquer un numéro de téléphone valide.";
  if (!f.service) e.service = "Veuillez choisir un service.";
  if (f.adresse.trim().length < 4)
    e.adresse = "Veuillez indiquer l'adresse du lieu d'intervention.";
  if (!f.frequence) e.frequence = "Veuillez choisir une fréquence.";
  if (f.message.length > 5000)
    e.message = "Le message est trop long (5000 caractères maximum).";
  return e;
};

const inputCls = (hasError) =>
  `w-full rounded-md border bg-white px-4 py-3 text-sm text-noir placeholder-neutral-400 outline-none transition-colors duration-300 ${
    hasError ? "border-red-400" : "border-neutral-300 focus:border-gold"
  }`;

const labelCls = "mb-2 block text-xs font-semibold text-noir/80";

const ErrorText = ({ children }) =>
  children ? <p className="mt-1.5 text-xs text-red-500">{children}</p> : null;

const scrollToForm = (e) => {
  e.preventDefault();
  document.getElementById("formulaire")?.scrollIntoView({ behavior: "smooth" });
};

export default function Contact() {
  const [params] = useSearchParams();
  const prefill = useMemo(() => serviceSlugToName(params.get("service") || ""), [params]);
  const [form, setForm] = useState({ ...EMPTY, service: prefill });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    setForm((f) => ({ ...f, service: prefill || f.service }));
  }, [prefill]);

  const set = (key) => (ev) => {
    setForm((f) => ({ ...f, [key]: ev.target.value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = validate(form);
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.champs) {
        setErrors(detail.champs);
      }
      setServerError(
        err?.response?.status === 503
          ? "Le service d'envoi de courriels est momentanément indisponible. Merci de réessayer dans quelques instants ou de nous appeler."
          : "Une erreur est survenue lors de l'envoi. Merci de réessayer ou de nous appeler directement."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Banner
        image={PHOTOS.contactBanner}
        alt="Hall d'accueil moderne et bien entretenu"
        eyebrow="Contact"
        title={["Demandez votre", "soumission."]}
        text="Décrivez-nous vos espaces et vos besoins : nous préparerons une soumission claire, détaillée et sans engagement."
        testid="contact-banner-title"
      >
        <GoldButton
          as="a"
          href="#formulaire"
          onClick={scrollToForm}
          data-testid="contact-banner-cta"
        >
          Remplir le formulaire
        </GoldButton>
      </Banner>

      {/* ---------------- COORDONNÉES + FORMULAIRE ---------------- */}
      <section className="bg-[#F4EFE6] py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[38%_62%] lg:px-10">
          {/* Coordonnées */}
          <motion.aside
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col rounded-lg border border-gold/25 bg-noir p-8 sm:p-10"
          >
            <h2 className="font-serif text-2xl tracking-[0.06em] text-gold sm:text-3xl">
              NOS COORDONNÉES
            </h2>
            <div className="mt-4 h-px w-14 bg-gold" />
            <p className="mt-5 text-[15px] leading-relaxed text-mist">
              Une question ? Une soumission ? N'hésitez pas à nous contacter,
              nous vous répondrons rapidement.
            </p>

            <div className="mt-9 space-y-6">
              <a href={PHONE_LINK} data-testid="contact-phone-link" className="group flex items-center gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-noir">
                  <Phone className="h-4 w-4" strokeWidth={1.6} />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-mist">
                    Téléphone
                  </span>
                  <span className="mt-1 block text-lg text-ivory transition-colors group-hover:text-gold">
                    {PHONE_DISPLAY}
                  </span>
                </span>
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="group flex items-center gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-noir">
                  <Mail className="h-4 w-4" strokeWidth={1.6} />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-mist">
                    Courriel
                  </span>
                  <span className="mt-1 block text-lg text-ivory transition-colors group-hover:text-gold">
                    {EMAIL}
                  </span>
                </span>
              </a>
              <div data-testid="contact-zone" className="flex items-center gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold">
                  <MapPin className="h-4 w-4" strokeWidth={1.6} />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-mist">
                    Zone desservie
                  </span>
                  <span className="mt-1 block text-lg text-ivory">{ZONE}</span>
                </span>
              </div>
            </div>
          </motion.aside>

          {/* Formulaire */}
          <motion.div
            id="formulaire"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="rounded-lg border border-neutral-200 bg-white p-8 shadow-[0_2px_12px_rgba(0,0,0,0.05)] sm:p-10"
          >
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                data-testid="form-success-message"
                className="flex min-h-[420px] flex-col items-center justify-center py-10 text-center"
              >
                <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1.2} />
                <h3 className="mt-6 font-serif text-2xl text-noir sm:text-3xl">
                  Votre demande a bien été envoyée.
                </h3>
                <p className="mt-4 max-w-md text-[15px] leading-relaxed text-neutral-500">
                  Merci {form.nom.split(" ")[0]}. Nous avons bien reçu votre
                  demande de soumission et nous vous répondrons dans les plus
                  brefs délais.
                </p>
                <button
                  type="button"
                  data-testid="form-reset-button"
                  onClick={() => {
                    setForm(EMPTY);
                    setSent(false);
                    setErrors({});
                  }}
                  className="mt-8 border-b border-gold pb-1 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#9a7b3f] transition-colors hover:text-gold"
                >
                  Envoyer une autre demande
                </button>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                <h2 className="font-serif text-2xl tracking-[0.04em] text-noir sm:text-3xl">
                  Envoyez-nous un message
                </h2>
                <div className="mt-2 h-px w-14 bg-gold" />

                <div className="mt-8 grid gap-x-6 gap-y-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nom" className={labelCls}>Nom complet *</label>
                    <input id="nom" data-testid="input-nom" type="text" value={form.nom} onChange={set("nom")} placeholder="Votre nom" className={inputCls(errors.nom)} />
                    <ErrorText>{errors.nom}</ErrorText>
                  </div>
                  <div>
                    <label htmlFor="entreprise" className={labelCls}>Entreprise / Immeuble *</label>
                    <input id="entreprise" data-testid="input-entreprise" type="text" value={form.entreprise} onChange={set("entreprise")} placeholder="Nom de l'entreprise ou de l'immeuble" className={inputCls(errors.entreprise)} />
                    <ErrorText>{errors.entreprise}</ErrorText>
                  </div>
                  <div>
                    <label htmlFor="courriel" className={labelCls}>Adresse courriel *</label>
                    <input id="courriel" data-testid="input-courriel" type="email" value={form.courriel} onChange={set("courriel")} placeholder="vous@entreprise.ca" className={inputCls(errors.courriel)} />
                    <ErrorText>{errors.courriel}</ErrorText>
                  </div>
                  <div>
                    <label htmlFor="telephone" className={labelCls}>Téléphone *</label>
                    <input id="telephone" data-testid="input-telephone" type="tel" value={form.telephone} onChange={set("telephone")} placeholder="438 000-0000" className={inputCls(errors.telephone)} />
                    <ErrorText>{errors.telephone}</ErrorText>
                  </div>
                  <div>
                    <label htmlFor="service" className={labelCls}>Service souhaité *</label>
                    <select id="service" data-testid="select-service" value={form.service} onChange={set("service")} className={inputCls(errors.service)}>
                      <option value="">Sélectionnez un service</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.name}>{s.name}</option>
                      ))}
                      <option value="Plusieurs services / Sur mesure">Plusieurs services / Sur mesure</option>
                    </select>
                    <ErrorText>{errors.service}</ErrorText>
                  </div>
                  <div>
                    <label htmlFor="frequence" className={labelCls}>Fréquence souhaitée *</label>
                    <select id="frequence" data-testid="select-frequence" value={form.frequence} onChange={set("frequence")} className={inputCls(errors.frequence)}>
                      <option value="">Sélectionnez une fréquence</option>
                      {FREQUENCES.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <ErrorText>{errors.frequence}</ErrorText>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="adresse" className={labelCls}>Adresse du lieu d'intervention *</label>
                    <input id="adresse" data-testid="input-adresse" type="text" value={form.adresse} onChange={set("adresse")} placeholder="Adresse complète du lieu à entretenir" className={inputCls(errors.adresse)} />
                    <ErrorText>{errors.adresse}</ErrorText>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="message" className={labelCls}>Votre message</label>
                    <textarea id="message" data-testid="textarea-message" rows={5} value={form.message} onChange={set("message")} placeholder="Superficie, particularités des lieux, horaires d'accès…" className={`${inputCls(errors.message)} resize-none`} />
                    <ErrorText>{errors.message}</ErrorText>
                  </div>
                </div>

                {/* Honeypot — invisible to humans */}
                <input type="text" name="site_web" value={form.site_web} onChange={set("site_web")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                {serverError && (
                  <div data-testid="form-error-message" className="mt-6 flex items-start gap-3 rounded-md border border-red-300 bg-red-50 px-5 py-4">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    <p className="text-sm text-red-600">{serverError}</p>
                  </div>
                )}

                <div className="mt-8 flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
                  <p className="max-w-xs text-[12px] leading-relaxed text-neutral-400">
                    Les champs marqués d'un astérisque (*) sont requis. Votre
                    demande est transmise directement à notre équipe.
                  </p>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="submit-button"
                    className="inline-flex w-full items-center justify-center gap-3 rounded-md bg-gold px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.18em] text-noir transition-colors duration-300 hover:bg-[#D3A45C] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {sending ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-noir/30 border-t-noir" />
                        Envoi en cours…
                      </>
                    ) : (
                      <>
                        Envoyer ma demande
                        <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* ---------------- BANDEAU SOUMISSION RAPIDE ---------------- */}
      <section className="bg-noir py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl px-6 text-center"
          data-testid="contact-quick-band"
        >
          <h2 className="font-serif text-2xl leading-snug text-gold sm:text-3xl">
            Besoin d'une soumission rapide ?
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-ivory/80 sm:text-base">
            Contactez-nous dès maintenant et recevez une réponse dans les plus
            brefs délais.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-5 sm:flex-row">
            <a
              href={PHONE_LINK}
              data-testid="contact-quick-phone"
              className="inline-flex items-center gap-2.5 text-lg font-medium tracking-[0.08em] text-ivory transition-colors hover:text-gold"
            >
              <Phone className="h-5 w-5 text-gold" />
              {PHONE_DISPLAY}
            </a>
            <GoldButton as="a" href="#formulaire" onClick={scrollToForm} data-testid="contact-quick-cta">
              Remplir le formulaire
            </GoldButton>
          </div>
        </motion.div>
      </section>
    </>
  );
}
